import React, {useState, useEffect, useRef, useMemo} from 'react';
import {
    Box,
} from '@mui/material';
import { useParams, useHistory, useLocation, Redirect } from 'react-router-dom';
import loadStripeJs from '../../../utils/loadStripe';
import { Elements } from '@stripe/react-stripe-js';

// Hooks
import { useProgramBuilder } from './hooks/useProgramBuilder';
import { useDqAiProgramChat } from './hooks/useDqAiProgramChat';

// Utils & Constants
import { parseMarkdown, stripMarkers, rebuildCurationState } from './utils/chat.utils';
import { WIZARD_INITIAL_STATE, PREVIEW_TABS, DUMMY_MODULES, _isDevEnv } from './constants/wizard.constants';

// External Services/Components
import { logoutUser } from '../../../services/auth.service';
import { LOCAL_STORAGE_KEYS, CHAT_TYPE, DQ_PRO_PLAN_KEY, USER_ACCESS_LEVEL, INSIDER_PROFILE_TYPE, LEARNING_PROGRAM_STATUS, TOAST_TYPES, WORKSPACE_REQUEST_STATUS, PAYER_ACTION } from '../../../utils/constants';
import { updateProgramRequestStatusService } from '../../../services/client.service';
import { useAuth } from '../../../context/auth';
import { useColorMode } from '../../../context/colorMode/colorMode.context';
import { toast } from '../../../utils/utils';
import useStyles from '../../../assets/styles/DQProV2ProgCreatorPreviewStyles';
import PoweredByDQDefault from '../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQWhite from '../../../assets/images/PoweredByDQ_white.png';
import RazProgressSVG from '../../../components/UI/SVG_IMGs/RazProgressSVG';
import {
    getLearningProgramPreviewService,
    getUnrestrictedLearningProgramPreviewService,
    getLearningProgramPlansService,
    getLearningProgramLinkStatusService,
    getLearningProgramPreviewStatusService,
    getLearningProgramStatusService,
    saveChatStateInsiderService,
    loadChatStateInsiderService,
    getActiveChatInsiderProgramService,
    getCurationGreetingService,
    getInsiderProgramService,
    isLearningProgramReadyService,
    publishLearningProgramService,
    linkLearningProgramService,
    getTemplateProgramPreviewService,
} from '../../../services/learning_programs.service';
import { getCreditBalanceService } from '../../../services/billing.service';
import { getStripeConfigService } from '../../../services_archived/app.service';

// UI Components
import ChatSidebar from './components/LearnCreator/ChatSidebar.comp';
import ProgramPreview from './components/LearnCreator/ProgramPreview.comp';
import PlansDialog from './components/PlansDialog.jsx';
import PublishDialog from './components/PublishDialog.jsx';
import PublishProgramDialogs from '../components/PublishProgramDialogs.comp';
import TopUpCreditsDialog from '../components/TopUpCreditsDialog.comp';
import BuildTopUpDialog from './components/LearnCreator/Dialogs/BuildTopUpDialog.comp';
import { checkoutNeed, shortfallLine, suggestedCreditTopUp } from './utils/credit_shortfall.utils';
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import ChangePasswordDialog from "../../../components/ChangePasswordDialog/ChangePasswordDialog.comp";
import SignupDialogV2 from './components/SignupDialogV2.comp.jsx';
import GuestLoginDialogV2 from './components/GuestLoginDialogV2.comp.jsx';

// Opening prompt for a brand-new conversation — used both when there's no
// active program at all, and when the workspace's "active" program turned out
// to be one that's already halted (see checkIfProgramFailed below): a dead
// build should never be restored as if it were still in progress.
/**
 * Fallback for the chatbot's opening message. The real text is served by
 * /curation_greeting from the chatbot's own prompt file, so a revision there reaches this
 * page without a client change; this copy is only shown if that request fails. It is the
 * general greeting (byte-identical to the server's GREETING): the page does not know the
 * workspace's solution here, and the server picks the solution's own greeting when it can.
 * Markdown, not HTML: the bot renderer escapes HTML before styling.
 */
const FRESH_START_FALLBACK =
    "Hi, I'm DQ AI, your program-building assistant. In the next few minutes I'll help you create a complete AI-digital literacy or online safety program: sessions, activities, and built-in assessments that show how your learners improved.\n\n" +
    "Let's start. Tell me what you want to teach, who it's for, where it fits in (a school subject, a training track), and which country it's for. For example:\n\n" +
    "- Create a cyberbullying program for Grade 4 students in India, for English class.\n" +
    "- Build an AI literacy program for secondary students in Singapore, for ICT class.\n" +
    "- Design a responsible generative AI program for a marketing team in the UK, as part of upskilling.";

/**
 * Whether a program has already halted — checked BEFORE restoring a
 * workspace's "active chat" so a dead program is never shown as if it were
 * still building.
 *
 * Deliberately narrow: only the content-phase per-session ProgressStatus of
 * -1 is trusted, and only once the outline itself has actually completed.
 * That's the one failure signal that has held up under real testing (the
 * same one the Program Dashboard's own single-shot check already relies on
 * without false positives). The top-level outline progress_status is NOT
 * checked here — a -1 outline reading has shown up for programs that were
 * genuinely still building normally, so trusting it here previously wiped
 * healthy, in-progress conversations. If the outline isn't complete yet,
 * this returns false (not failed) and the normal resume/poll flow handles it.
 */
const checkIfProgramFailed = async (programId) => {
    try {
        const statusRes = await getLearningProgramPreviewStatusService(programId);
        const isOutlineComplete = statusRes?.data?.progress_status === 7
            || (statusRes?.data?.is_outline_ready && statusRes?.data?.generation_initiated);
        if (!isOutlineComplete) return false;
        const contentRes = await getLearningProgramStatusService(programId);
        const contentStatusData = contentRes?.data?.contentStatus || {};
        return Object.values(contentStatusData).some(
            (s) => typeof s === 'object' && s.ProgressStatus === -1
        );
    } catch (_) {
        return false; // can't tell — don't block the restore on a network hiccup
    }
};

const DQProV2LearnCreator = ({ isPublicPreview: isPublicPreviewProp = false, readOnly: readOnlyProp = false }) => {
    const [isChatOpen, setIsChatOpen] = useState(false);       // controls DOM mount
    const [chatAnimPhase, setChatAnimPhase] = useState('idle'); // drives the BUTTON animation
    const [panelPhase, setPanelPhase] = useState('idle');       // drives the PANEL clip-path (decoupled so button can appear while panel still collapses)
    const classes = useStyles();
    const { isDark } = useColorMode();
    const PoweredByDQ = isDark ? PoweredByDQWhite : PoweredByDQDefault;
    const history = useHistory();
    const location = useLocation();
    const { authUser, setAuthToken, setAuthUser } = useAuth();
    const { code: dq_module_code } = useParams();
    const _storedUseTemplate = localStorage.getItem(LOCAL_STORAGE_KEYS.dqpro_v2_use_template);
    const _initialUseTemplate = _isDevEnv && (_storedUseTemplate === null ? true : _storedUseTemplate === 'true');

    const [userMessage, setUserMessage] = useState(location.state?.userMessage || localStorage.getItem(LOCAL_STORAGE_KEYS.dqpro_user_message) || '');
    const teacherDash = location.state?.teacherDash || false;

    // Public preview (/slide_preview/:code) or sample programs template preview.
    // Behaves as a pure viewer: no chat, no build, no edit/publish.
    const isPublicPreview = isPublicPreviewProp || location.state?.isPublicPreview === true;
    const readOnly = isPublicPreview || readOnlyProp || location.state?.readOnly === true;
    /*
     * View only — a school / network previewing a program its PARTNER built
     * (SW-46 / SNW-64: "Schools cannot edit the program, they can only view a
     * preview"). Like readOnly — no chat, save, build, edit or publish — but
     * the program is a normal one, so its data comes from the normal preview
     * services, not the template ones readOnly switches to. The partner's chat
     * is theirs, and the API refuses it to anyone else.
     */
    const viewOnly = !readOnly && location.state?.viewOnly === true;

    // UI State
    const [activeTab, setActiveTab] = useState(1);
    const [programData, setProgramData] = useState(null);
    const [programDetails, setProgramDetails] = useState(null);
    const [modules, setModules] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [showPlans, setShowPlans] = useState(false);
    const [showPublishDialog, setShowPublishDialog] = useState(false);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [showSaveProgramDialog, setShowSaveProgramDialog] = useState(false);
    const [showSignupDialog, setShowSignupDialog] = useState(false);
    const [wizard, setWizard] = useState({ ...WIZARD_INITIAL_STATE, CourseCode: '', UseTemplate: _initialUseTemplate });
    const seededRequestIdRef = useRef(null);
    // The program already started for the seeded request (empty on a first build).
    // Together these keep each request's chat SEPARATE: a page opened to build
    // request X must never restore the workspace-wide "active" chat, which may
    // belong to a different request.
    const seededProgramCodeRef = useRef('');
    // Pre-fill when arriving from a School's Learning Program request (owner
    // clicked "Build Program"). ADDITIVE and inert: it only seeds the wizard +
    // the chat INPUT box (it does NOT set userMessage, so nothing auto-sends and
    // the normal build flow is completely unaffected). The owner reviews the
    // pre-filled prompt and sends it themselves.
    //
    // seededRequestIdRef marks this page instance as "opened to build request X".
    // Delivery on publish is bound to the program code produced here, so
    // abandoning this build and publishing an unrelated program can never mark
    // the school's request delivered.
    useEffect(() => {
        const pr = location.state && location.state.programRequest;
        if (!pr) return;
        // Remember that THIS page instance was opened to build that request, so
        // the stash can be bound to the program code once one exists.
        seededRequestIdRef.current = pr.request_id || null;
        seededProgramCodeRef.current = String(pr.programCode || '').trim();
        setWizard((w) => ({
            ...w,
            ...(pr.Theme ? { Theme: pr.Theme } : {}),
            ...(pr.NumSessions ? { NumSessions: Number(pr.NumSessions) } : {}),
            ...(pr.SessionDuration ? { SessionDuration: Number(pr.SessionDuration) } : {}),
            ...(pr.SubjectContextTopic ? { SubjectContextTopic: pr.SubjectContextTopic } : {}),
            ...(pr.CulturalSensitivityDetails ? { CulturalSensitivityDetails: pr.CulturalSensitivityDetails } : {}),
        }));
        // Compose a ready-to-send prompt ONLY on a first build. Once this request
        // has a program the owner is RESUMING: the conversation is restored below,
        // so re-typing the opening prompt would invite them to send it a second
        // time and start the topic over.
        const isResuming = !!seededProgramCodeRef.current;
        // Not when the prompt arrives already sent from the LearnCreator start
        // page (LA-PW-13Oct-02) — it would sit in the box a second time.
        if (pr.Theme && !isResuming && !location.state.userMessage) {
            let prompt = `Create a Child Online Safety program about "${pr.Theme}"`;
            if (pr.grade_level) prompt += ` for ${pr.grade_level}`;
            if (pr.NumSessions) prompt += `, ${pr.NumSessions} session${Number(pr.NumSessions) > 1 ? 's' : ''}`;
            if (pr.SessionDuration) prompt += ` of ${pr.SessionDuration} minutes each`;
            prompt += '.';
            if (pr.SubjectContextTopic) prompt += ` Connect it to ${pr.SubjectContextTopic}.`;
            if (pr.CulturalSensitivityDetails) prompt += ` Please consider these local sensitivities: ${pr.CulturalSensitivityDetails}.`;
            if (pr.AdditionalInfo) prompt += ` Other information from the school: ${String(pr.AdditionalInfo).trim().replace(/[.\s]+$/, '')}.`;
            setChatInput(prompt);
        }
        // programRequest is deliberately LEFT in the router state. It is what tells
        // the binding effect below which request this build belongs to; clearing it
        // (as an earlier version did) meant that on any re-mount the request id was
        // gone, program_code was never persisted back to the request, and the page
        // could only ever re-seed instead of resuming. Re-seeding is prevented by
        // the isResuming check above, not by destroying the state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [isTabTransitioning, setIsTabTransitioning] = useState(false);
    const [currentPlanKey, setCurrentPlanKey] = useState(null);
    const [isPlanLoading, setIsPlanLoading] = useState(!isPublicPreview);
    const [userClickedPreview, setUserClickedPreview] = useState(isPublicPreview || readOnly || viewOnly);
    const [isResumingBuild, setIsResumingBuild] = useState(false);
    const [chatLocked, setChatLocked] = useState(false);
    const [isProgramEditable, setIsProgramEditable] = useState(true);
    // Publish state — LEARNING_PROGRAM_STATUS.DRAFT or PUBLISHED, possibly other
    // in-between values in future. Kept in sync with programData once loaded/published.
    const [programStatus, setProgramStatus] = useState(LEARNING_PROGRAM_STATUS.DRAFT);
    // Whether content generation is fully complete — a prerequisite for publishing.
    const [contentReady, setContentReady] = useState(false);
    const [publishing, setPublishing] = useState(false);
    // null = both dialogs closed; 'confirm' = "are you sure?" step; 'success' = post-publish step.
    const [publishStage, setPublishStage] = useState(null);
    // School whose request THIS publish fulfilled (P1 Workspace, slide 155) —
    // switches the publish-success dialog to the "linked to (school)" copy.
    const [deliveredSchoolName, setDeliveredSchoolName] = useState('');
    // org_code of the school this publish delivered to — lets the success dialog
    // link the program to THAT school directly instead of sending the owner to
    // the client list to find it (bug report, slide 40).
    const [deliveredOrgCode, setDeliveredOrgCode] = useState('');
    const [linkingToSchool, setLinkingToSchool] = useState(false);

    // Keep programStatus in sync whenever programData is (re)loaded with a status field.
    // Publishing is one-way (no unpublish), so status can only ever move forward —
    // Math.max guards against a stale/cached programData snapshot (e.g. restored from
    // a persisted session saved just before a publish) silently regressing an already-
    // published program back to Draft in the UI.
    useEffect(() => {
        if (programData && programData.status !== undefined && programData.status !== null) {
            setProgramStatus((prev) => Math.max(prev, Number(programData.status)));
        }
    }, [programData]);
    // Edit Program Info dialog — lifted to the page so its trigger can live inside
    // the chat (ChatSidebar) while the dialog itself still renders in ContentArea.
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [showTopUpDialog, setShowTopUpDialog] = useState(false);
    const [topUpAmount, setTopUpAmount] = useState(50);
    // What the card top-up was opened for: the missing amount, and what resumes once paid.
    const [topUpShortfallText, setTopUpShortfallText] = useState('');
    const topUpOnPaidRef = useRef(null);
    // The DQ Token / credit checkout (partner and direct owners): { info, need, onPaid }.
    const [buildTopUp, setBuildTopUp] = useState(null);
    const [topUpClientSecret, setTopUpClientSecret] = useState(null);
    const [creditData, setCreditData] = useState(null);
    const [publishableKey, setPublishableKey] = useState(null);

    const stripePromise = useMemo(
        () => (publishableKey ? loadStripeJs(publishableKey) : null),
        [publishableKey]
    );

    // Expansion state for the builder to update
    const [openModules, setOpenModules] = useState({ 0: true });
    const [openUnits, setOpenUnits] = useState({ '0-0': true });

    // Fetch Stripe config and credit balance once on mount.
    // Skip for guest users and unauthenticated users: the guest_user auth endpoint does NOT
    // set up E2EE keypairs, so billing/config endpoints return E2EE-encrypted responses that
    // cannot be decrypted — which would trigger invokeApi's force-logout path.
    useEffect(() => {
        if (isPublicPreview) return;
        const isGuest = !authUser || authUser.profile_type === INSIDER_PROFILE_TYPE.GUEST;
        const hasKeypair = !!localStorage.getItem(LOCAL_STORAGE_KEYS.e2ee_krypto_pair_obj);
        if (isGuest || !hasKeypair) return;

        const fetchStripeConfig = async () => {
            try {
                const [cfgRes, creditRes] = await Promise.all([
                    getStripeConfigService(),
                    getCreditBalanceService(),
                ]);
                const pk = cfgRes?.data?.publishableKey || cfgRes?.publishableKey || null;
                setPublishableKey(pk);
                if (creditRes?.data) setCreditData(creditRes.data);
            } catch (_) {}
        };
        fetchStripeConfig();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Refs
    const chatEndRef = useRef(null);
    const hasInitRef = useRef(false);
    const chatInputRef = useRef(null);  // used by TimeOptionsMessage to focus the textarea

    const saveTimerRef = useRef(null);
    const isSavingRef = useRef(false);
    const chatTimersRef = useRef([]);
    const chatOpenRafRef = useRef(null);
    const panelRef = useRef(null);
    // Tracks whether a build was ever active in this session.
    // Auto-collapse only fires when a build transitions true → false, not on initial load.
    const buildWasActiveRef = useRef(false);
    const currentStepRef = useRef(null);
    // Set to true by handleWizardSave so the next debounced auto-save skips chatHistory.
    const skipNextAutoSaveRef = useRef(false);
    // Set to true while the dashboard→preview restore is in flight (async API calls).
    // Prevents the auto-save from firing with empty state before restoration completes.
    const isRestoringStateRef = useRef(false);

    // ── Chat widget multi-phase open / close animation ────────────────────────
    const clearChatTimers = () => {
        chatTimersRef.current.forEach(t => clearTimeout(t));
        chatTimersRef.current = [];
        if (chatOpenRafRef.current) { cancelAnimationFrame(chatOpenRafRef.current); chatOpenRafRef.current = null; }
    };

    // Compute inline styles for the toggle bubble: a quick cross-fade in place, no travel.
    // It used to rise/slide across the screen to "merge" into the chat header, which assumed
    // one fixed corner-anchored panel — but the panel's position/size varies (chatDockedPanel
    // vs chatFloatingPanel in DQProV2ProgCreatorPreviewStyles.js), and a resting spot other
    // than the bubble's natural bottom-right corner read as out of place on its own. Fading
    // out where it already sits sidesteps both problems and works for either panel layout.
    const getBubbleStyle = () => {
        switch (chatAnimPhase) {
            case 'pre-open':
                return { pointerEvents: 'none', transition: 'none' };
            case 'opening':
                // Stays in this state for as long as the chat is open — the panel's own
                // reveal (panelPhase) runs independently and at its own pace.
                return {
                    transform: 'scale(0.4)',
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: 'transform 0.25s ease, opacity 0.2s ease',
                };
            default: // 'idle'
                return {};
        }
    };

    const openChat = () => {
        clearChatTimers();
        setIsChatOpen(true);
        setPanelPhase('pre-open');
        setChatAnimPhase('pre-open');
        // Double rAF: first frame mounts the panel, second measures it and triggers transition
        chatOpenRafRef.current = requestAnimationFrame(() => {
            chatOpenRafRef.current = requestAnimationFrame(() => {
                setPanelPhase('opening');            // panel reveals bottom-to-top (header hidden)
                setChatAnimPhase('opening');         // bubble fades out in place, same beat
                chatOpenRafRef.current = null;
                // Settle as soon as the panel's own transition finishes (chatFloatingPanel's
                // phase-opening rule) rather than waiting out the old multi-second merge
                // choreography — that delay no longer serves any visible animation.
                const t1 = setTimeout(() => {
                    setPanelPhase('open');
                }, 260);
                chatTimersRef.current.push(t1);
            });
        });
    };

    const closeChat = () => {
        clearChatTimers();
        setPanelPhase('closing');    // panel collapses (220ms wipe down)
        setChatAnimPhase('idle');    // button springs in IMMEDIATELY — no waiting
        // Unmount panel after collapse animation finishes
        const t = setTimeout(() => { setIsChatOpen(false); setPanelPhase('idle'); }, 260); // matches the 220ms collapse
        chatTimersRef.current.push(t);
    };

    // Save chat state to backend (async)
    const saveChatState = (extraData = {}) => {
        if (readOnly || viewOnly || !programIdState || isSavingRef.current) return;

        // When triggered right after a wizard-only edit, skip the full auto-save entirely —
        // the wizard was already persisted directly without chatHistory.
        if (skipNextAutoSaveRef.current) {
            skipNextAutoSaveRef.current = false;
            return;
        }

        // Block auto-save while the dashboard→preview restore is in flight.
        // initWithExistingProgram sets programIdState (triggering this effect) but the
        // async loadChatStateInsiderService call may not have resolved yet — saving now
        // would overwrite server state_data with empty chatHistory / default wizard values.
        if (isRestoringStateRef.current) return;

        // For URL-loaded programs, the dq_module_code useEffect restores the wizard
        // asynchronously. If the auto-save fires before the wizard is restored (race condition),
        // it would overwrite the correct state_data with WIZARD_INITIAL_STATE defaults.
        // Guard: only auto-save for URL-loaded programs once the wizard has real data.
        if (dq_module_code && !wizard.Age && !wizard.Theme && !wizard.Title) return;

        const stateData = {
            chatHistory,
            wizard,
            activeTab,
            programData,
            modules,
            programIdState,
            curationState: getCurationState(),
            ...(currentStepRef.current !== null ? { step: currentStepRef.current } : {}),
            ...extraData,
        };


        // Persist to server (async)
        isSavingRef.current = true;
        saveChatStateInsiderService(programIdState, stateData)
            .then(() => { isSavingRef.current = false; })
            .catch((e) => {
                console.warn('saveChatStateInsiderService failed:', e);
                isSavingRef.current = false;
            });
    };

    // Custom Hooks
    
    // The builder hook handles the multi-step building logic and polls for status.
    // Build, description, and content-generation are all server-side; the hook only polls.
    const { isProgramBuilding, step1Progress, contentProgress, buildProgressStatus, handleBuildProgram, resumeBuild, cancelBuild } = useProgramBuilder(
        (cb) => setChatHistory(cb),
        setModules,
        setProgramData,
        setOpenModules,
        setOpenUnits,
        () => setActiveTab(4),  // onOutlineReady  → prepare for build
        () => setActiveTab(1),  // onContentReady  → switch to Learn tab by default
        () => { currentStepRef.current = 17; },  // onBuildServiceCall → mark step before build
        currentPlanKey,  // so polling can apply free/default plan early-completion logic
    );

    // The chat hook posts one turn at a time and applies the server's reply.
    // When the server reports the build has started it calls handleBuildProgram(programCode),
    // which only polls — no client-side build/content-gen calls are made.
    const { curationStage, buildStarted, chatHistory, setChatHistory, isBotThinking, sendMessage, startFromLanding, initWithExistingProgram, programIdState, insufficientCredits, confirmBuildCost, declineBuildCost, retryAfterTopUp, retryAfterCredits, retryBuild, getCurationState, restoreCurationState } = useDqAiProgramChat(
        handleBuildProgram,
        setWizard,
    );

    /**
     * Top Up from a credit shortfall (the chat's cards, or a refused rebuild). Opens the
     * payment window that matches who pays for the build (info.payer): DQ Tokens for a
     * partner owner, the organisation's credit checkout for a direct school or network
     * owner, the card credit top-up for a legacy workspace owner — pre-filled with what
     * is missing. Only owners are offered Top Up; the API refuses anyone else.
     *
     * @param {object}   info    the shortfall ({ payer, shortfall, dqt })
     * @param {Function} onPaid  what resumes once the payment is confirmed
     */
    const openTopUp = (info, onPaid = null) => {
        if (!publishableKey || !stripePromise) {
            toast(TOAST_TYPES.WARN, 'Payments unavailable', 'Payments cannot be taken right now. Please try again later.');
            return;
        }
        const action = info?.payer?.action;
        if (action === PAYER_ACTION.BUY_DQT || action === PAYER_ACTION.BUY_DIRECT_CREDITS) {
            setBuildTopUp({ info, need: checkoutNeed(info), onPaid });
            return;
        }
        setTopUpAmount(suggestedCreditTopUp(info?.shortfall));
        setTopUpShortfallText(shortfallLine(info));
        topUpOnPaidRef.current = onPaid;
        setShowTopUpDialog(true);
    };

    const closeCardTopUp = () => {
        setShowTopUpDialog(false);
        setTopUpClientSecret(null);
        setTopUpShortfallText('');
    };

    // Bind the stashed request to the program code produced by THIS build, so
    // publishing any other program can never mark that request delivered.
    // NOTE: must live BELOW the chat-hook destructuring — programIdState is
    // read in the dependency array during render (TDZ crash if placed earlier).
    useEffect(() => {
        const requestId = seededRequestIdRef.current;
        const code = dq_module_code || programIdState;
        if (!requestId || !code) return;
        try {
            const raw = sessionStorage.getItem('dq_pending_program_request_id');
            if (!raw) return;
            let stash;
            try { stash = JSON.parse(raw); } catch { stash = { id: raw }; }
            if (!stash || stash.id !== requestId || stash.program_code === code) return;
            sessionStorage.setItem('dq_pending_program_request_id', JSON.stringify({
                ...stash, program_code: code,
            }));
            // Also persist the binding server-side so it survives a closed tab:
            // the request keeps IN_PROGRESS and remembers which program is being
            // built for it. Best effort — never disturbs the build.
            updateProgramRequestStatusService({
                request_id: requestId,
                status: WORKSPACE_REQUEST_STATUS.IN_PROGRESS,
                program_code: code,
            }).catch(() => {});
        } catch { /* ignore storage errors */ }
    }, [dq_module_code, programIdState]);

    /*
     * Keep the newest message in view.
     *
     * The first paint of a RESTORED conversation, and every re-open of the
     * panel, jump straight to the end: a smooth scroll across a long history
     * either crawls or never arrives, which is why reopening a built program
     * showed the greeting at the top instead of where the person left off.
     * Messages arriving during a live conversation still animate.
     */
    const hasAutoScrolledRef = useRef(false);
    useEffect(() => {
        if (!isChatOpen) { hasAutoScrolledRef.current = false; return; }
        if (!chatEndRef.current) return;
        const behavior = hasAutoScrolledRef.current ? 'smooth' : 'auto';
        chatEndRef.current.scrollIntoView({ behavior, block: 'end' });
        if (chatHistory.length) hasAutoScrolledRef.current = true;
    }, [chatHistory, isBotThinking, isChatOpen]);

    // Auto-collapse chat when a build finishes so the preview is fully visible.
    // Gate on buildWasActiveRef so this never fires on initial load for existing programs
    // (where isProgramBuilding is already false and modules are already populated).
    useEffect(() => {
        if (isProgramBuilding) {
            /*
             * Close as soon as the build STARTS, not only when it ends.
             *
             * Once the engine is generating content the conversation has nothing
             * left to ask, and the five build cards are the thing worth watching.
             *
             * Only on the transition into building — so if the person re-opens
             * the chat while it runs, this does not keep shutting it on them.
             */
            if (!buildWasActiveRef.current) {
                buildWasActiveRef.current = true;
                if (isChatOpen) {
                    chatTimersRef.current.forEach(t => clearTimeout(t));
                    chatTimersRef.current = [];
                    if (chatOpenRafRef.current) { cancelAnimationFrame(chatOpenRafRef.current); chatOpenRafRef.current = null; }
                    setPanelPhase('closing');
                    setChatAnimPhase('idle');
                    const tStart = setTimeout(() => { setIsChatOpen(false); setPanelPhase('idle'); }, 260);
                    chatTimersRef.current.push(tStart);
                }
            }
            return;
        }
        if (buildWasActiveRef.current) {
            /*
             * Clear the flag for ANY build that has ended, not only one that
             * produced modules. A build that failed left the preview empty and
             * the flag stuck on, so the next run read as "already building" and
             * the chat no longer closed itself on Proceed — the person had to
             * minimise it by hand (Dr. Song's feedback, slide 11).
             */
            buildWasActiveRef.current = false;
            // Nothing was produced, so there is no preview worth uncovering;
            // leave the panel as the person left it.
            if (!modules || modules.length === 0) return;
            chatTimersRef.current.forEach(t => clearTimeout(t));
            chatTimersRef.current = [];
            if (chatOpenRafRef.current) { cancelAnimationFrame(chatOpenRafRef.current); chatOpenRafRef.current = null; }
            setPanelPhase('closing');
            setChatAnimPhase('idle');
            const t = setTimeout(() => { setIsChatOpen(false); setPanelPhase('idle'); }, 260); // matches the 220ms collapse
            chatTimersRef.current.push(t);
        }
    }, [isProgramBuilding, modules]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear the "resuming" loader once the build state is determined:
    // either building started, or the program was already complete (programData loaded).
    useEffect(() => {
        if (!isResumingBuild) return;
        const buildActive = isProgramBuilding || (!!programData && !userClickedPreview);
        const buildDone   = userClickedPreview || chatHistory.some(m => m.formStep === 17);
        if (buildActive || buildDone) setIsResumingBuild(false);
    }, [isProgramBuilding, programData, userClickedPreview, chatHistory]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Auto-open chat whenever the user lands on this page ───────────────────
    // Level 22 gets the same real AI-builder chat as a Teacher's LearnCreator
    // (see schoolWorkspace.nav.js) — no separate skip here.
    useEffect(() => {
        if (readOnly || viewOnly) return; // viewer-only, no chat/build flow
        openChat();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── On mount: decide whether to start fresh or restore an in-progress session ─
    useEffect(() => {
        if (hasInitRef.current) return;
        hasInitRef.current = true;

        // Requested up front so it is normally resolved by the time the restore decides
        // to show it; a failure just falls back to the bundled copy.
        const greetingPromise = getCurationGreetingService().catch(() => null);

        const restoreState = (saved, programId) => {
            const chat = saved.chatHistory || [];

            console.log('[V2 init] restoring', chat.length, 'messages for program', programId);

            if (chat.length) setChatHistory(chat);
            if (saved.wizard)       setWizard({ ...saved.wizard, UseTemplate: _initialUseTemplate });
            if (saved.activeTab)    setActiveTab(saved.activeTab);
            if (saved.programData)  setProgramData(saved.programData);
            if (saved.modules)      setModules(saved.modules);

            // Default to Learn (1) if building is done, else use saved tab
            const isDone = chat.some(m => m.formStep === 17);
            if (saved.activeTab) {
                setActiveTab(saved.activeTab);
            } else if (isDone) {
                setActiveTab(1);
            }
            // If build is already complete, skip the "content building" intro screen
            if (isDone) {
                setUserClickedPreview(true);
                setChatLocked(true);
            }

            // Restore curation refs — server also strips curationState,
            // so rebuild from wizard when it's missing.
            const curationState = saved.curationState
                || rebuildCurationState(chat, saved.wizard);
            restoreCurationState(curationState);

            initWithExistingProgram(programId);
            // Only resume polling if the build was actually initiated by the server.
            // If the user only reached the time-options step (no time submitted yet),
            // serverBuildInitiated and buildTriggered are both false — there is nothing
            // to resume, and calling resumeBuild here would set isBuildingRef=true which
            // blocks handleBuildProgram when the user eventually picks a time option.
            const buildWasStarted = curationState.serverBuildInitiated || curationState.buildTriggered;
            if (buildWasStarted) {
                resumeBuild(programId, true);
            }
        };

        const init = async () => {
            if (isPublicPreview) {
                // Public preview — viewer-only, bypass active chat restore and authenticated calls
                return;
            }
            // URL-loaded preview should not restore a different active chat program.
            // That restore path can overwrite wizard values used by EditProgramInfoDialog.
            if (dq_module_code) {
                initWithExistingProgram(dq_module_code);
                return;
            }

            /*
             * A specific in-progress conversation, resumed from the programs
             * list. Deliberately NOT programCode: that is the handle on a
             * BUILT program and its branch below fetches the module and the
             * outline, neither of which a draft has. This one is a chat id,
             * and it is used further down to aim the ordinary restore path at
             * one conversation instead of the workspace-wide active chat.
             */
            const resumeDraftId = location.state?.resumeDraftId;
            if (resumeDraftId) {
                // A landing-page message left in storage would otherwise start a
                // new build on top of the draft the person just asked to reopen.
                localStorage.removeItem(LOCAL_STORAGE_KEYS.dqpro_user_message);
            }

            // ── Navigated from workspace dashboard with a specific program ──
            // Check this BEFORE userMessage so a stale localStorage value doesn't
            // trigger a new program build over the existing one.
            const incomingProgramCode = location.state?.programCode;
            if (incomingProgramCode) {
                setLoading(true);
                cancelBuild();
                // Block auto-save until we have fully restored state from the server.
                // initWithExistingProgram (called below) sets programIdState immediately,
                // which would trigger a debounced save with empty state before the async
                // loadChatStateInsiderService resolves — guard against that race.
                isRestoringStateRef.current = true;
                // Clear any stale user message so it doesn't start a new build
                localStorage.removeItem(LOCAL_STORAGE_KEYS.dqpro_user_message);
                setUserMessage('');
                // Keep programCode in router state for a template: it is the only
                // handle on which program to show (no :code in this route), so
                // clearing it would blank the page on any re-mount or refresh.
                if (!readOnly && !viewOnly) {
                    history.replace({ ...location, state: { ...location.state, programCode: undefined } });
                }

                // The program is already built — show its content immediately,
                // skip the building/preview-card view entirely.
                setUserClickedPreview(true);
                setChatLocked(true);
                // Learn by default; a caller may open another tab, e.g. My Programs'
                // "View Assessment" opens Assess (2) — LA-PW,SNW,SW,TW-19Oct-01.
                setActiveTab([1, 2, 3].includes(location.state?.openTab) ? location.state.openTab : 1);

                // Fetch program data so the right-side tabs render immediately
                try {
                    const res = readOnly
                        ? await getTemplateProgramPreviewService(incomingProgramCode)
                        : await getLearningProgramPreviewService(incomingProgramCode);
                    if (res?.data) {
                        setProgramData(res.data);
                        const lang = Object.keys(res.data.program_layout_previews || {})[0] || 'en-US';
                        const preview = res.data.program_layout_previews[lang]?.program_layout_preview;
                        if (preview?.modules) setModules(preview.modules);
                    }
                } catch (_) {}

                // A template has no chat/wizard of the viewer's own, and both of the
                // lookups below are scoped to the caller's insider_id — for someone
                // else's template they can only 404. Nothing in read-only mode needs
                // them (no edit dialog, no publish), so skip the block entirely.
                // NOTE: initWithExistingProgram further down must still run — it sets
                // programIdState, which is what feeds the code to ContentArea →
                // DQProProgPreview. Returning early here would leave the page blank.
                if (!readOnly && !viewOnly) {
                // Fetch insider program details AND chat state in parallel.
                // Wizard priority: state_data.wizard → program_details (same fallback
                // logic used by the dq_module_code path so the edit dialog always
                // opens with fully-populated fields).
                try {
                    const [insiderResult, savedResult] = await Promise.allSettled([
                        getInsiderProgramService(incomingProgramCode),
                        loadChatStateInsiderService(incomingProgramCode),
                    ]);

                    const insiderRes = insiderResult.status === 'fulfilled' ? insiderResult.value : null;
                    const savedRes   = savedResult.status  === 'fulfilled' ? savedResult.value  : null;

                    const insiderDetails = insiderRes?.data?.learning_program?.program_details;
                    if (insiderDetails) setProgramDetails(insiderDetails);

                    // Unlike the dq_module_code path below, this route (opening an existing
                    // program via router state, e.g. from a program list) never read the
                    // canonical publish status — programStatus stayed stuck at its Draft
                    // default forever, regardless of whether the program was published.
                    const insiderStatus = insiderRes?.data?.learning_program?.status;
                    if (insiderStatus !== undefined && insiderStatus !== null) {
                        setProgramStatus((prev) => Math.max(prev, Number(insiderStatus)));
                    }

                    const saved = savedRes?.data?.state_data || savedRes?.data;

                    if (saved?.chatHistory?.length) {
                        setChatHistory(saved.chatHistory);
                    }

                    // Wizard: prefer state_data.wizard; fall back to program_details so the
                    // edit dialog always shows fully-populated fields even when state_data
                    // had its wizard cleared.
                    const wizardFromChat    = saved?.wizard;
                    const restoredWizard    = wizardFromChat || insiderDetails || null;
                    if (restoredWizard) {
                        setWizard(prev => ({
                            ...prev,
                            ...restoredWizard,
                            CourseCode: restoredWizard?.CourseCode || incomingProgramCode,
                            UseTemplate: _initialUseTemplate,
                        }));
                    }

                    if (saved?.chatHistory?.length || saved?.curationState) {
                        const curationState = saved.curationState
                            || rebuildCurationState(saved.chatHistory || [], restoredWizard);
                        restoreCurationState(curationState);
                    }
                } catch (_) {}
                } // end if (!readOnly)

                initWithExistingProgram(incomingProgramCode);
                setLoading(false);
                // Do NOT reset isRestoringStateRef here.
                // For the dashboard→preview path the program is already built and
                // the chat is locked — there is nothing the page's auto-save needs
                // to persist.  EditProgramInfoDialog has its own saveChatStateInsiderService
                // call that merges with existing state_data independently.
                // Leaving isRestoringStateRef.current = true permanently blocks the
                // auto-save for the lifetime of this component instance (the ref is
                // re-initialised to false on the next mount).
                return;
            }

            // `userMessage` is read from state captured at mount, so clearing
            // storage above cannot retract it — the draft has to say so here.
            if (userMessage && !resumeDraftId) {
                // Came from landing page right after signup → clear old state, run curation stream.
                // Cancel any polling loop that may be running for the previous program FIRST.
                cancelBuild();
                // Clear location.state FIRST so a page refresh won't re-trigger startFromLanding.
                history.replace({ ...location, state: {} });
                localStorage.removeItem(LOCAL_STORAGE_KEYS.dqpro_user_message);
                startFromLanding(userMessage);
                return;
            }

            /*
             * "Start New Program" means START NEW — do not go looking for an
             * active chat to restore.
             *
             * Clearing the old program is not enough on its own: "active chat"
             * is workspace-wide, so on a workspace with several drafts deleting
             * one simply promotes the next and the person is dropped back into
             * a conversation they did not ask for. Saying so explicitly is the
             * only thing that holds regardless of how many drafts exist.
             */
            if (location.state?.startNewProgram) {
                cancelBuild();
                history.replace({ ...location, state: {} });
                const greetingRes = await greetingPromise;
                setChatHistory([{
                    type: CHAT_TYPE.BOT,
                    text: greetingRes?.data?.greeting_markdown || FRESH_START_FALLBACK,
                }]);
                return;
            }

            // ── Server-based restore (handles other devices / cleared cache) ──
            try {
                // Opened to build a SPECIFIC school request? Then the workspace-wide
                // "active chat" is the wrong thing to restore — it may belong to a
                // different request, which is how two requests ended up sharing one
                // conversation. Resolve this request's OWN program instead:
                //   • it already has one  → restore that chat,
                //   • it has none yet     → start clean.
                // Requests therefore stay independent of each other.
                const greetingRes = await greetingPromise;
                const freshStart = () => ({
                    type: CHAT_TYPE.BOT,
                    text: greetingRes?.data?.greeting_markdown || FRESH_START_FALLBACK,
                });

                const seededRequestId = seededRequestIdRef.current;
                /*
                 * active_chat answers "the one chat this workspace was last in",
                 * which is the right default on a cold open and the wrong answer
                 * when the person has just clicked one draft out of several.
                 * Both overrides below skip the call rather than second-guess it.
                 */
                const activeRes = resumeDraftId
                    ? { data: { program_id: resumeDraftId } }
                    : seededRequestId
                        ? { data: { program_id: seededProgramCodeRef.current || null } }
                        : await getActiveChatInsiderProgramService();
                const activeProgramId = activeRes?.data?.program_id;
                console.warn('[V2 restore DEBUG] activeProgramId:', activeProgramId);
                if (!activeProgramId) {
                    // Fresh workspace with no active program AND no userMessage.
                    console.warn('[V2 restore DEBUG] -> no activeProgramId, fresh start');
                    setChatHistory([freshStart()]);
                    return;
                }

                // Try to load saved chat state.
                const savedRes = await loadChatStateInsiderService(activeProgramId);
                // The load endpoint may return state_data contents directly in `data`
                // Fallback to nested `state_data` for safety.
                const saved = savedRes?.data?.state_data || savedRes?.data;
                console.warn('[V2 restore DEBUG] saved:', saved, 'chatHistory length:', saved?.chatHistory?.length);

                if (!saved || !saved.chatHistory?.length) {
                    // Program exists but has no history saved yet — the build may be
                    // in progress or already dead. Check before committing to "start
                    // polling" so an old, halted program isn't shown as if it were
                    // still building.
                    const failed = await checkIfProgramFailed(activeProgramId);
                    console.warn('[V2 restore DEBUG] -> no saved chatHistory, checkIfProgramFailed:', failed);
                    if (failed) {
                        setChatHistory([freshStart()]);
                        return;
                    }
                    setChatHistory([freshStart()]);
                    initWithExistingProgram(activeProgramId);
                    resumeBuild(activeProgramId, true);
                    return;
                }

                const rebuiltCuration  = rebuildCurationState(saved.chatHistory || [], saved.wizard);
                const restoredCuration = saved.curationState || rebuiltCuration;
                const buildWasStarted  = !!(restoredCuration?.serverBuildInitiated || restoredCuration?.buildTriggered);
                console.warn('[V2 restore DEBUG] saved.curationState:', saved.curationState, 'rebuiltCuration:', rebuiltCuration, 'buildWasStarted:', buildWasStarted);
                // NOTE: captured session timing alone no longer implies a build — with the
                // cost-confirmation gate it means "awaiting the user's Proceed". Only resume
                // polling when the build was actually started (server-initiated or triggered),
                // otherwise we'd poll for a program that was never built.
                //
                // An already-halted program must never be restored as if it were still
                // building — check BEFORE restoring so the dead conversation is never
                // even flashed on screen.
                const preCheckFailed = buildWasStarted && await checkIfProgramFailed(activeProgramId);
                console.warn('[V2 restore DEBUG] buildWasStarted && checkIfProgramFailed:', preCheckFailed);
                if (preCheckFailed) {
                    setChatHistory([freshStart()]);
                    return;
                }
                restoreState(saved, activeProgramId);
                if (buildWasStarted) setIsResumingBuild(true);
                if (buildWasStarted) resumeBuild(activeProgramId, true);
            } catch (err) {
                console.warn('[V2 init] server restore failed:', err);
            }
        };

        init();
        // Cancel any in-progress polling when the component unmounts
        return () => cancelBuild();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Save chat state whenever it changes (debounced)
    useEffect(() => {
        if (!programIdState) return;
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => { saveChatState(); }, 800);
        return () => clearTimeout(saveTimerRef.current);
    }, [chatHistory, wizard, activeTab, programData, modules, programIdState]); // eslint-disable-line react-hooks/exhaustive-deps


    // Fetch existing program if code provided in URL
    useEffect(() => {
        if (dq_module_code) {
            setChatLocked(true);
            setUserClickedPreview(true);
            setActiveTab(1);

            if (isPublicPreview) {
                setLoading(true);
                getUnrestrictedLearningProgramPreviewService(dq_module_code).then(res => {
                    if (res?.data) {
                        setProgramData(res.data);
                        const lang = Object.keys(res.data.program_layout_previews || {})[0] || 'en-US';
                        const preview = res.data.program_layout_previews[lang]?.program_layout_preview;
                        if (preview?.modules) setModules(preview.modules);
                    }
                }).catch(err => {
                    console.error('Failed to load unrestricted preview:', err);
                }).finally(() => {
                    setLoading(false);
                });
                return;
            }

            getLearningProgramPreviewService(dq_module_code).then(res => {
                if (res?.data) {
                    setProgramData(res.data);
                    const lang = Object.keys(res.data.program_layout_previews || {})[0] || 'en-US';
                    const preview = res.data.program_layout_previews[lang]?.program_layout_preview;
                    if (preview?.modules) setModules(preview.modules);
                }
            });

            getInsiderProgramService(dq_module_code).then(res => {
                if (res?.data?.learning_program?.program_details) {
                    setProgramDetails(res.data.learning_program.program_details);
                }
                if (res?.data?.learning_program?.status !== undefined && res.data.learning_program.status !== null) {
                    // Math.max: guard against read-replica lag showing a stale pre-publish
                    // status right after a same-session publish (status only moves forward).
                    setProgramStatus((prev) => Math.max(prev, Number(res.data.learning_program.status)));
                }
            }).catch(() => {});

            // Restore wizard for edit prefill.
            // Completed programs can return empty chat_state data, so fallback to
            // learning_program.program_details from insider_program endpoint.
            Promise.allSettled([
                // View only: the partner's chat is not the viewer's to load.
                viewOnly ? Promise.resolve(null) : loadChatStateInsiderService(dq_module_code),
                getInsiderProgramService(dq_module_code),
            ]).then(([savedResult, insiderResult]) => {
                const savedRes = savedResult.status === 'fulfilled' ? savedResult.value : null;
                const insiderRes = insiderResult.status === 'fulfilled' ? insiderResult.value : null;

                const saved = savedRes?.data?.state_data || savedRes?.data || {};
                const wizardFromChat = saved?.wizard;
                const wizardFromProgram = insiderRes?.data?.learning_program?.program_details;
                const restoredWizard = wizardFromChat || wizardFromProgram;

                if (restoredWizard) {
                    setWizard(prev => ({
                        ...prev,
                        ...restoredWizard,
                        CourseCode: restoredWizard?.CourseCode || dq_module_code,
                        UseTemplate: _initialUseTemplate,
                    }));
                }

                if (saved?.chatHistory?.length) {
                    setChatHistory(saved.chatHistory);
                }
                if (saved?.chatHistory?.length || saved?.curationState) {
                    const curationState = saved.curationState
                        || rebuildCurationState(saved.chatHistory || [], saved.wizard || restoredWizard);
                    restoreCurationState(curationState);
                }
            }).catch(() => {});
        }
    }, [dq_module_code]); // eslint-disable-line react-hooks/exhaustive-deps

    // Check link status only when preview is active and outline is ready.
    // This avoids calling the API during from-scratch creation before preview exists.
    useEffect(() => {
        const programCodeForStatus = dq_module_code || programIdState;
        if (readOnly || viewOnly) return; // owner-scoped; never linkable/editable here
        if (!programCodeForStatus) return;
        if (!userClickedPreview) return;
        if (programData?.is_outline_ready !== true) return;

        getLearningProgramLinkStatusService(programCodeForStatus)
            .then(res => {
                setIsProgramEditable(!!res?.data?.is_editable);
            })
            .catch(() => {});
    }, [dq_module_code, programIdState, userClickedPreview, programData?.is_outline_ready]);

    // Fetch content-readiness — a prerequisite for enabling the Publish button.
    // Guarded on programData: right after the first message, sendMessage() creates
    // an insider-program shell and sets programIdState immediately — well before the
    // backend's actual Learning Program record exists. Calling is_ready that early
    // 400s with "Learning program not found" and surfaces a spurious error toast.
    // programData only gets populated once real program content exists server-side
    // (either the outline/build has streamed back, or an existing program was loaded),
    // which is also the earliest point a Learning Program record could plausibly exist.
    useEffect(() => {
        const programCodeForPublish = dq_module_code || programIdState;
        if (readOnly || viewOnly) return; // never published from the viewer's session
        if (!programCodeForPublish || !programData) return;
        let cancelled = false;
        isLearningProgramReadyService(programCodeForPublish)
            .then((res) => {
                if (!cancelled) setContentReady(Boolean(res?.data?.is_ready));
            })
            .catch(() => {
                if (!cancelled) setContentReady(false);
            });
        return () => { cancelled = true; };
    }, [dq_module_code, programIdState, programData]);

    const isProgramPublished = programStatus === LEARNING_PROGRAM_STATUS.PUBLISHED;

    const handlePublish = async () => {
        const programCodeForPublish = dq_module_code || programIdState;
        if (!programCodeForPublish || publishing || isProgramPublished || !contentReady) return;
        setPublishing(true);
        try {
            const response = await publishLearningProgramService(programCodeForPublish);
            if (response?.data) {
                const newStatus = LEARNING_PROGRAM_STATUS.PUBLISHED;
                setProgramStatus(newStatus);
                const publishedProgramData = programData ? { ...programData, status: newStatus } : programData;
                setProgramData(publishedProgramData);
                saveChatState({ programData: publishedProgramData });
                setPublishStage('success');
                toast(TOAST_TYPES.SUCCESS, 'Published', response.data.already_published
                    ? 'This program is already published.'
                    : 'Learning program published successfully.');

                // If this program was built for a School's learning-program request,
                // mark that request DELIVERED (the API emails the school). The request
                // is resolved SERVER-SIDE from the program code, so it works in any
                // session; the browser stash only supplies the dialog's school name.
                // No-op for ordinary programs. Best effort — never disturbs publishing.
                updateProgramRequestStatusService({
                    program_code: programCodeForPublish,
                    status: WORKSPACE_REQUEST_STATUS.BUILT,
                }).catch(() => {});
                try {
                    const rawStash = sessionStorage.getItem('dq_pending_program_request_id');
                    if (rawStash) {
                        // New format: JSON {id, school_name, org_code, program_code};
                        // legacy: plain id string.
                        let schoolName = '';
                        let schoolOrgCode = '';
                        let boundCode = null;
                        try {
                            const parsed = JSON.parse(rawStash);
                            if (parsed && parsed.id) {
                                schoolName = parsed.school_name || '';
                                schoolOrgCode = parsed.org_code || '';
                                boundCode = parsed.program_code || null;
                            }
                        } catch { /* legacy plain-id stash */ }
                        if (!boundCode || boundCode === programCodeForPublish) {
                            sessionStorage.removeItem('dq_pending_program_request_id');
                            // Publishing DELIVERS the program and emails the school,
                            // but it does not link it — the owner still has to do
                            // that before the school can open it. The success dialog
                            // says exactly that and links straight to this school.
                            if (schoolName) setDeliveredSchoolName(schoolName);
                            if (schoolOrgCode) setDeliveredOrgCode(schoolOrgCode);
                        }
                    }
                } catch { /* ignore storage errors */ }
            }
        } catch (err) {
            // invokeApi already surfaces the backend error message as a toast.
            setPublishStage(null);
        } finally {
            setPublishing(false);
        }
    };

    // Opens the "are you sure?" confirmation dialog — the actual publish call only
    // happens once the user confirms via the dialog's "Publish" button.
    const openPublishConfirm = () => {
        const programCodeForPublish = dq_module_code || programIdState;
        if (!programCodeForPublish || publishing || isProgramPublished || !contentReady) return;
        setPublishStage('confirm');
    };

    // Already on the program's own materials/preview page — just close the dialog.
    const handleGoToMaterials = () => setPublishStage(null);

    /**
     * Link the just-published program to the school that requested it.
     *
     * When this publish fulfilled a school's request we know exactly which org to
     * link to, so we link it here and stay put — the owner should not be dropped
     * on the client list to hunt for the school (bug report, slide 40). Same
     * endpoint the requests page and the client list use.
     * Fallback for an ordinary publish (no requesting school): the client list,
     * exactly as before.
     */
    const handleLinkToSchool = async () => {
        const programCodeForPublish = dq_module_code || programIdState;
        if (!deliveredOrgCode || !programCodeForPublish) {
            setPublishStage(null);
            history.push('/dq_pro/clients/list');
            return;
        }
        if (linkingToSchool) return;
        setLinkingToSchool(true);
        try {
            const res = await linkLearningProgramService(deliveredOrgCode, [programCodeForPublish]);
            if (res?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, 'Linked',
                    `${deliveredSchoolName || 'The school'} can now open this program from their workspace.`);
                setPublishStage(null);
            }
        } catch {
            /* invokeApi surfaces the backend error (e.g. no license seats) */
        } finally {
            setLinkingToSchool(false);
        }
    };

    // Fetch the user's current plan on mount to decide whether to show Upgrade button
    useEffect(() => {
        if (isPublicPreview) {
            setIsPlanLoading(false);
            return;
        }
        setIsPlanLoading(true);
        getLearningProgramPlansService()
            .then(res => {
                if (res?.data?.current_plan?.key) {
                    setCurrentPlanKey(res.data.current_plan.key);
                }
            })
            .catch(() => {})
            .finally(() => setIsPlanLoading(false));
    }, [isPublicPreview]);

    const handleSendMessage = () => {
        const trimmed = chatInput.trim();
        if (!trimmed) return;
        setChatInput('');
        sendMessage(trimmed);
    };

    const handleChatKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const [anchorEl, setAnchorEl] = useState(null);

    const handleResetPassword = () => {
        setAnchorEl(null);
        setChangePasswordOpen(true);
    };

    const handleLogout = async () => {
        setAnchorEl(null);
        try {
            await logoutUser();
            setAuthToken(null);
            setAuthUser(null);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.auth_token);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.auth_user);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.refresh_token);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.user_email);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.dqpro_v2_google_auth_redirect);
            history.push('/login');
        } catch (_) {}
    };

    const handleUseTemplateToggle = () => {
        setWizard(prev => {
            const next = !prev.UseTemplate;
            localStorage.setItem(LOCAL_STORAGE_KEYS.dqpro_v2_use_template, String(next));
            return { ...prev, UseTemplate: next };
        });
    };

    // ── Readiness guards ───────────────────────────────────────────────────────
    const isOutlineReady = !!programData && !!(programIdState || dq_module_code);

    // Level 22 (partial-access, single-institute) users get the same real
    // AI-builder chat as a Teacher's LearnCreator (schoolWorkspace.nav.js
    // routes them to this same page for that reason). They still can't edit
    // or publish an already-built program here — see canEditProgram /
    // canPublishProgram below — but chat/build is fully available.
    const isLevel22 = authUser?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;

    // Chat is locked once the build completes (formStep 17 enters the history),
    // or when the page is opened to preview an already-built program.
    const isChatLocked = chatLocked || chatHistory.some(m => m.formStep === 17);

    // Content phase is the period AFTER outline is ready but BEFORE the user
    // clicks "Click to Preview" (or formStep 17 lands, whichever comes first).
    const isContentBuilding = !isPublicPreview && isOutlineReady && !userClickedPreview && !isProgramBuilding;

    /*
     * Close the chat when the CONTENT build starts too.
     *
     * The effect further up watches isProgramBuilding, which covers the outline
     * phase only — once the outline lands that flag drops and this one takes
     * over for the rest of the build, which is most of the wait. Without this
     * the panel sat open over the progress cards for that whole stretch.
     *
     * Its own ref, so the two never fight: each closes once, on its own
     * transition, and re-opening the chat during a build stays the person's
     * choice.
     */
    const contentBuildClosedRef = useRef(false);
    useEffect(() => {
        if (!isContentBuilding) { contentBuildClosedRef.current = false; return; }
        if (contentBuildClosedRef.current) return;
        contentBuildClosedRef.current = true;
        if (!isChatOpen) return;
        chatTimersRef.current.forEach((t) => clearTimeout(t));
        chatTimersRef.current = [];
        if (chatOpenRafRef.current) { cancelAnimationFrame(chatOpenRafRef.current); chatOpenRafRef.current = null; }
        setPanelPhase('closing');
        setChatAnimPhase('idle');
        const t = setTimeout(() => { setIsChatOpen(false); setPanelPhase('idle'); }, 260);
        chatTimersRef.current.push(t);
    }, [isContentBuilding]); // eslint-disable-line react-hooks/exhaustive-deps

    // step1Progress and contentProgress come directly from useProgramBuilder state.
    // Overall: outline phase = 40%, content phase contributes another 30%.
    const overallProgress = Math.min(100, 40 + Math.round(contentProgress * 0.3));

    const programMetaParts = [
        wizard?.SessionDuration && `${wizard.SessionDuration} min`,
        wizard?.NumSessions && `${wizard.NumSessions} session${wizard.NumSessions > 1 ? 's' : ''}`,
        ...(Array.isArray(wizard?.SelectedFrameList)
            ? wizard.SelectedFrameList.map(f => (typeof f === 'string' ? f : f?.label)).filter(Boolean)
            : []),
    ].filter(Boolean);
    const programMeta = programMetaParts
        .slice(0, 3)
        .map(p => String(p).replace(/\{\{detected_?frameworks=[^}]*\}\}/g, '').trim())
        .join(' · ');
    const programDisplayTitle = programData?.full_name || wizard?.Title || wizard?.Theme || 'My Learning Program';

    const handleTabChange = (targetTab) => {
        if ([1, 2, 3].includes(targetTab) && activeTab !== targetTab) {
            setIsTabTransitioning(true);
            setTimeout(() => {
                setIsTabTransitioning(false);
            }, 1500); 
        }
        setActiveTab(targetTab);
    };

    return (
        <Box className={[
            classes.pageWrapper,
            // No reserved column any more: the left panel was removed (Dr. Park,
            // 10 Sep) and the chat is centred as the page body, so there is
            // nothing beside it to hold clear of.
        ].filter(Boolean).join(' ')}>
            <Box className={classes.mainContainer}>
                {/* Main Content Area */}
                <ProgramPreview
                    classes={classes}
                    isOutlineReady={isOutlineReady}
                    isContentBuilding={isContentBuilding}
                    programDisplayTitle={programDisplayTitle}
                    programMeta={programMeta}
                    overallProgress={overallProgress}
                    authUser={authUser}
                    history={history}
                    teacherDash={teacherDash}
                    currentPlanKey={currentPlanKey}
                    setShowPublishDialog={setShowPublishDialog}
                    setShowSaveProgramDialog={setShowSaveProgramDialog}
                    setShowPlans={setShowPlans}
                    anchorEl={anchorEl}
                    setAnchorEl={setAnchorEl}
                    handleResetPassword={handleResetPassword}
                    handleLogout={handleLogout}
                    isProgramBuilding={isProgramBuilding}
                    step1Progress={step1Progress}
                    contentProgress={contentProgress}
                    buildProgressStatus={buildProgressStatus}
                    isResumingBuild={isResumingBuild}
                    setUserClickedPreview={setUserClickedPreview}
                    // Whether "Preview learning program" has been pressed — the banner
                    // ticks its Preview step on that click.
                    userClickedPreview={userClickedPreview}
                    curationStage={curationStage}
                    buildStarted={buildStarted}
                    chatHistory={chatHistory}
                    wizard={wizard}
                    onWizardSave={(updatedWizard) => setWizard(prev => ({ ...prev, ...updatedWizard }))}
                    activeTab={activeTab}
                    handleTabChange={handleTabChange}
                    isTabTransitioning={isTabTransitioning}
                    dq_module_code={dq_module_code}
                    programIdState={programIdState}
                    programData={programData}
                    programDetails={programDetails}
                    modules={modules}
                    isPlanLoading={isPlanLoading}
                    isProgramEditable={isProgramEditable}
                    readOnly={readOnly}
                    isPublicPreview={isPublicPreview}
                    canEditProgram={!readOnly && !viewOnly && !isLevel22 && isOutlineReady && isProgramEditable && !isProgramBuilding && !isProgramPublished}
                    canPublishProgram={!readOnly && !viewOnly && !isLevel22 && isOutlineReady && contentReady && !isProgramPublished && !isProgramBuilding}
                    publishing={publishing}
                    onPublishProgram={openPublishConfirm}
                    editDialogOpen={editDialogOpen}
                    setEditDialogOpen={setEditDialogOpen}
                    // Edit program information: once paid, the dialog re-prices the edit and the person saves again.
                    onTopUpCredits={(info, onPaid) => openTopUp(info, onPaid)}
                    onEditSaved={() => {
                        setUserClickedPreview(false);
                        setProgramData(null);
                        setModules([]);
                        handleBuildProgram(programIdState || dq_module_code);
                    }}
                    isChatOpen={isChatOpen}
                />
            </Box>

            {/* Floating Chat Widget — visible on Learn tab (1) and during wizard/build
                phase, including for Level 22 (school) users building via LearnCreator. */}
            {!readOnly && !viewOnly && (activeTab === 1 || !isOutlineReady) && (
                <>
                    {/* Panel: mounted while isChatOpen; clip-path phase class drives the reveal */}
                    {isChatOpen && (
                        <Box
                            ref={panelRef}
                            /*
                              * Slides 5-6. Docked as a right rail while the program is
                              * still being built, floating once there is content to
                              * read underneath it.
                              */
                            className={[
                                classes.chatFloatingPanel,
                                /*
                                 * Centred for the whole build view, not only until the
                                 * outline lands. "Five parts built and aligned … Preview
                                 * learning program" is still that screen — the cards are
                                 * the page and the conversation belongs in the middle of
                                 * it, as it is during the chat. It moves to the right
                                 * rail on "Preview learning program", once there is
                                 * program content to read underneath.
                                 *
                                 * Same pair of flags the solid background below keys on:
                                 * isProgramBuilding covers the outline build (when
                                 * isOutlineReady is still false) and isContentBuilding
                                 * covers everything after it up to that click.
                                 */
                                (!isOutlineReady || isContentBuilding) ? classes.chatDockedPanel : '',
                                // Build cards sit behind the panel; only then does it need
                                // a background of its own.
                                // BOTH build flags. isProgramBuilding covers the outline
                                // build; once the outline lands it flips false and
                                // isContentBuilding takes over — so keying on the first
                                // alone left the panel transparent over the cards for the
                                // whole content build, which is most of the wait.
                                (isProgramBuilding || isContentBuilding) ? classes.chatPanelSolid : '',
                                panelPhase !== 'idle' ? `phase-${panelPhase}` : '',
                            ].filter(Boolean).join(' ')}
                            // The panel sits at 1301, above MUI dialogs (1300). While the
                            // DQ Token top-up is open it drops just below them, so that
                            // dialog and the checkout it opens are on top and clickable
                            // (LA-PW-19Oct-02).
                            style={buildTopUp ? { zIndex: 1299 } : undefined}
                        >
                            <ChatSidebar
                                classes={classes}
                                curationStage={curationStage}
                                PoweredByDQ={PoweredByDQ}
                                chatHistory={chatHistory}
                                isBotThinking={isBotThinking}
                                isChatLocked={isChatLocked}
                                chatInput={chatInput}
                                setChatInput={setChatInput}
                                handleChatKeyDown={handleChatKeyDown}
                                chatInputRef={chatInputRef}
                                handleSendMessage={handleSendMessage}
                                sendMessage={sendMessage}
                                chatEndRef={chatEndRef}
                                isDevEnv={_isDevEnv}
                                useTemplate={wizard.UseTemplate}
                                onUseTemplateToggle={handleUseTemplateToggle}
                                onTopUpCredits={(info) => openTopUp(info, () => retryAfterTopUp())}
                                onRetryCredits={retryAfterCredits}
                                onConfirmBuild={confirmBuildCost}
                                onDeclineBuild={declineBuildCost}
                                onRetryBuild={retryBuild}
                                onClose={closeChat}
                                showContent={panelPhase === 'open' || panelPhase === 'closing'}
                                canEditProgram={isOutlineReady && isProgramEditable && !isProgramBuilding && !isProgramPublished}
                                onEditProgram={() => {
                                    closeChat();
                                    setEditDialogOpen(true);
                                }}
                            />
                        </Box>
                    )}
                    {/* Toggle bubble: cross-fades out in place (no travel) as the panel opens.
                        Also fully hidden (display:none) once the panel is settled-open, so it
                        can't peek out at the bottom-right and overlap the edit pencil / send
                        button. */}
                    <Box
                        className={`${classes.chatToggleBtn}${chatAnimPhase === 'idle' ? ' btn-idle' : ''}`}
                        style={{ ...getBubbleStyle(), ...(panelPhase === 'open' ? { display: 'none' } : {}) }}
                        onClick={openChat}
                    >
                        <RazProgressSVG color="#FF6900" style={{ width: '80%', height: '80%', filter: 'drop-shadow(0 0 1.5px #FF6900)' }} />
                    </Box>
                </>
            )}

            {/* Dialog components */}
            <PlansDialog
                open={showPlans}
                onClose={() => setShowPlans(false)}
                onSelectPlan={(planKey) => {
                    // Update local plan state so the Upgrade button hides immediately
                    setCurrentPlanKey(planKey);
                }}
                currentPlanKey={currentPlanKey}
                classes={classes}
            />
            {stripePromise && showTopUpDialog && (
                <Elements stripe={stripePromise}>
                    <TopUpCreditsDialog
                        open={showTopUpDialog}
                        onClose={closeCardTopUp}
                        shortfallText={topUpShortfallText}
                        topUpAmount={topUpAmount}
                        setTopUpAmount={setTopUpAmount}
                        topUpClientSecret={topUpClientSecret}
                        setTopUpClientSecret={setTopUpClientSecret}
                        dqInsiderInfo={null}
                        creditData={creditData}
                        getCreditBalanceService={getCreditBalanceService}
                        setCreditData={setCreditData}
                        setLoading={setLoading}
                        onTopUpSuccess={() => { if (topUpOnPaidRef.current) topUpOnPaidRef.current(); }}
                    />
                </Elements>
            )}
            <BuildTopUpDialog
                open={!!buildTopUp}
                info={buildTopUp?.info}
                need={buildTopUp?.need}
                publishableKey={publishableKey}
                onClose={() => setBuildTopUp(null)}
                onPurchased={() => {
                    const onPaid = buildTopUp?.onPaid;
                    setBuildTopUp(null);
                    getCreditBalanceService()
                        .then((r) => { if (r?.data) setCreditData(r.data); })
                        .catch(() => {});
                    if (onPaid) onPaid();
                }}
            />
            <PublishDialog
                open={showPublishDialog} 
                onClose={() => setShowPublishDialog(false)} 
                onPublish={() => history.push({
                    pathname: '/dq_pro/teacher-dashboard',
                    state: { publishedProgram: true }
                })} 
                classes={classes} 
                programTitle={programData?.program_title} 
            />
            <PublishProgramDialogs
                stage={publishStage}
                publishing={publishing}
                onGoBack={() => setPublishStage(null)}
                onConfirmPublish={handlePublish}
                onGoToMaterials={handleGoToMaterials}
                onLinkToSchool={handleLinkToSchool}
                deliveredSchoolName={deliveredSchoolName}
                linkingToSchool={linkingToSchool}
            />
            <ChangePasswordDialog
                open={changePasswordOpen}
                onClose={() => setChangePasswordOpen(false)}
            />

            <GuestLoginDialogV2
                open={showSaveProgramDialog} 
                onClose={() => setShowSaveProgramDialog(false)}
                onSignupClick={() => {
                    setShowSaveProgramDialog(false);
                    setShowSignupDialog(true);
                }}
                onSuccess={() => {
                    setShowPlans(true); 
                }}
            />

            <SignupDialogV2 
                open={showSignupDialog} 
                onClose={() => setShowSignupDialog(false)}
                onLoginClick={() => {
                    setShowSignupDialog(false);
                    setShowSaveProgramDialog(true);
                }}
                onSuccess={() => {
                    // Logic to handle success (e.g., refresh data or show plans)
                    setShowPlans(true); 
                }}
            />

            <LoaderWithBackDrop loading={loading || processingPayment} />
        </Box>
    );
};

export const DQProProgCreationPreviewV2 = DQProV2LearnCreator;
export { DQProV2LearnCreator };
export default DQProV2LearnCreator;
